import React, { useCallback, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Container from '../components/common/Container';
import { useTranslation } from 'react-i18next';
import Button from '../components/common/Button';
import LegalPersonReferenceDetailModal from '../components/modal/LegalPersonReferenceDetailModal';
import MemoModal from '../components/modal/MemoModal';
import LegalPersonReferenceItem from '../components/tableItems/legalPersonReferenceItem';
import AddLegalPersonReference from '../components/regalPersonReference/AddLegalPersonReference';
import { useMutation, useQuery } from '@apollo/client';
import { GET_CORPORATE_INFO, SAVE_VERIFICATION_MEMO } from "../graphql/mutations";
import { GET_LEGAL_PERSON_REFERENCE } from "../graphql/queries";
import Loading from '../components/common/Loading';

const LegalPersonReferenceAdd: React.FC = () => {
    const navigate = useNavigate();
    const { t } = useTranslation('verification');
    const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
    const [isMemoModalOpen, setIsMemoModalOpen] = useState(false);
    const [isShowAddLegalPersonReference, setIsShowAddLegalPersonReference] = useState<boolean>(false);
    const [corporateNumber, setCorporateNumber] = useState<string>('');
    const [corporateInfo, setCorporateInfo] = useState<{ name: string; address: string } | null>(null);
    const [selectedIndex, setSelectedIndex] = useState<number>(0);
    const [fieldName, setFieldName] = useState<string>('');

    // Query for the latest 5 legal person references
    const { loading: referencesLoading, error: referencesError, data: referencesData } = useQuery(GET_LEGAL_PERSON_REFERENCE);

    const [saveVerificationMemo] = useMutation(SAVE_VERIFICATION_MEMO, {
        onCompleted: () => {
            console.log('Legal person reference created successfully');
            window.location.reload();
        },
        onError: (error) => {
            console.error('Error creating legal person reference:', error);
            alert(t('error creating legal person reference'));
        }
    });

    const [getCorporateInfo, { loading, error }] = useMutation(GET_CORPORATE_INFO, {
        onCompleted: (data) => {
            setCorporateInfo(data.getCorporateInfoFromCorporateNumber);
            setIsShowAddLegalPersonReference(true);
        },
        onError: (err) => {
            console.error('GraphQL Error:', err);
            alert(t('error fetching corporate info'));
        }
    });

    // Get the latest 5 items sorted by receivedDate in descending order
    const getLatestFiveItems = useCallback(() => {
        if (!referencesData || !referencesData.allLegalPersonReference) return [];
        
        // Sort by receivedDate in descending order (newest first)
        const sortedData = [...referencesData.allLegalPersonReference].sort((a, b) => {
            const dateA = new Date(a.legalPersonReference_receivedDate || 0);
            const dateB = new Date(b.legalPersonReference_receivedDate || 0);
            return dateB.getTime() - dateA.getTime();
        });
        
        // Return only the first 5 items
        return sortedData.slice(0, 5);
    }, [referencesData]);

    const handleCloseDetailModal = useCallback(() => setIsDetailModalOpen(false), []);
    const handleOpenDetailModal = useCallback((idx: number) => {
        setSelectedIndex(idx);
        setIsDetailModalOpen(true);
        setFieldName("legalPersonReference");
    }, []);
    const handleCloseMemoModal = useCallback(() => setIsMemoModalOpen(false), []);
    
    const handleUpdateMemo = (value: string) => {
        saveVerificationMemo({
            variables: {
                verificationMemo: value,
                verificationPublicId: referencesData.allLegalPersonReference[selectedIndex].legalPersonReference_verificationPublicId,
            }
        })
        .then(
            () => setIsMemoModalOpen(false)
        ).catch(
            err => console.log(err)
        )
    }
    const showAllLegalPersonReference = useCallback(() => navigate('/corporate-reference-list'), []);
    const handleCorporateInformationSearch = useCallback(() => {
        if(corporateNumber === '') {
            alert(t('enter corporate number'));
            return;
        }

        getCorporateInfo({ variables: { corporateNumber } });
    }, [corporateNumber]);

    return (
        <Container label={t('legal person reference')}>
            <LegalPersonReferenceDetailModal
                isOpen={isDetailModalOpen}
                onClose={handleCloseDetailModal}
                referenceDetailData={referencesData && referencesData.allLegalPersonReference && selectedIndex >= 0 ? referencesData.allLegalPersonReference[selectedIndex] : null}
                fieldName={fieldName}
            />
            <MemoModal
                isOpen={isMemoModalOpen}
                onClose={handleCloseMemoModal}
                onConfirm={handleUpdateMemo}
                memoData={referencesData && referencesData.allLegalPersonReference[selectedIndex].legalPersonReference_verificationMemo}
                companyName={referencesData && referencesData.allLegalPersonReference[selectedIndex].corporateName}
            />
            <div className="flex justify-between items-center">
                <h3>{t('legal person reference history')}</h3>
                <Button
                    label={t('show all')}
                    onClick={showAllLegalPersonReference}
                    addedClass='h-[46px]'
                />
            </div>
            {referencesLoading ? (
                <Loading />
            ) : referencesError ? (
                <p className="text-center mt-4 text-red-500">Error: {referencesError.message}</p>
            ) : (
                <table>
                    <thead>
                    <tr>
                        <th>{t('corporate name')}</th>
                        <th>{t('corporate number')}</th>
                        <th>{t('reference person')}</th>
                        <th>{t('reference datetime')}</th>
                        <th>{t('verified datetime')}</th>
                        <th>{t('legal person verification status')}</th>
                        <th>{t('aml risk check status')}</th>
                        <th>メモ</th>
                    </tr>
                    </thead>
                    <tbody>
                    {getLatestFiveItems().map((value, index) => {
                        return (
                            <LegalPersonReferenceItem
                                key={index}
                                item={value}
                                selectedPage={1}
                                idx={index}
                                showDetail={handleOpenDetailModal}
                                showMemoModal={(value) => {
                                    setSelectedIndex(value)
                                    setIsMemoModalOpen(true)}
                                }
                            />
                        )
                    })}
                    </tbody>
                </table>
            )}
            <h3>{t('new legal person reference')}</h3>
            <div className="form-group">
                <label className="block text-base mb-[5px]">{t('corporate number')}</label>
                <div className="flex gap-2 items-center">
                    <input
                        className="light-border p-2 rounded"
                        style={{
                            border: "1px solid #ddd"
                        }}
                        value={corporateNumber}
                        onChange={(e) => setCorporateNumber(e.target.value)}
                    />
                    <Button
                        label={t('corporate information search')}
                        onClick={handleCorporateInformationSearch}
                    />
                    <a href="https://www.houjin-bangou.nta.go.jp" target="_blank" className="link">{t('look up corporate number')}</a>
                </div>
                {loading && <p>{t('loading')}...</p>}
                {error && <p className="text-red-500">{t('error fetching corporate info')}</p>}
                {isShowAddLegalPersonReference && <AddLegalPersonReference name={corporateInfo?.name ?? ''} address={corporateInfo?.address ?? ''} corporateNumber={corporateNumber}/>}
            </div>
        </Container>
    );
};

export default LegalPersonReferenceAdd;
